import { Controller } from "@hotwired/stimulus";

// Makes Enter activate an `<a class="item">` inside a Fomantic dropdown menu.
//
// Why this is needed
// ------------------
// Fomantic's dropdown module binds keydown to the dropdown element and, on
// Enter with the menu open, calls its OWN item-click routine and then
// `event.preventDefault()` (dropdown.js:1638-1649). That routine sets a value
// and hides the menu; it never touches the item's `href`, and the
// preventDefault stops the browser from activating the link itself. So arrow
// keys move the highlight, Tab moves focus onto the link — and Enter does
// nothing on either. Verified in the running app, both paths.
//
// Every `<a class="item">` dropdown in this app has that gap (the roster's sort
// menus, Bulk Actions, New Collab). It is only a REGRESSION here, because AI
// Search's two exports used to be plain focusable buttons in the page header,
// so this controller is scoped to the element that needs it rather than
// installed document-wide. Widening it later is a one-line change; doing it now
// would put every dropdown in the app inside a PR about an export button.
//
// Capture phase, so this runs before the module's bubble-phase handler and the
// navigation is underway before anything cancels the event.
export default class extends Controller {
  connect() {
    this.onKeydown = (event) => this.handleKeydown(event);
    this.element.addEventListener("keydown", this.onKeydown, true);
  }

  disconnect() {
    this.element.removeEventListener("keydown", this.onKeydown, true);
  }

  handleKeydown(event) {
    if (event.key !== "Enter") return;

    // Only once the menu is open. On a closed dropdown Enter is what OPENS it,
    // and a `.selected` item can be left over from an earlier visit — acting on
    // it here would download a file the user only meant to open a menu.
    if (!this.element.classList.contains("visible")) return;

    // Either the link the user tabbed onto, or the one the arrow keys have
    // highlighted (focus stays on the dropdown itself in that case).
    const target = event.target instanceof Element ? event.target : null;
    const link =
      target?.closest("a.item") ||
      this.element.querySelector(".menu > a.item.selected");

    if (!link || !this.element.contains(link)) return;

    // preventDefault before click(), not after: on a focused link the browser
    // would otherwise synthesise its own activation click too, and these hrefs
    // are file downloads — two clicks is two downloads.
    event.preventDefault();
    link.click();
  }
}
